import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import Modal from 'common/components/organisms/Modal';
import { FlexContainer } from 'common/components/atoms';
import Label_T from 'common/typography/Label/Label';
import styled from 'styled-components';
import CalendarFilter from 'common/components/Chart/components/CalendarFilter';
import Chart from 'react-google-charts';
import H3_T from 'common/typography/H3/H3';
import LoadingSvg from 'common/images/LoadingSvg';
import DownloadSvg from 'common/images/DownloadSvg';
import { downloadAsCSV } from 'common/helpers';
import H4_T from 'common/typography/H4/H4';
import { splitDeviceInfoLogs } from '../utils/deviceInfoLogChartData';
import * as sensorActions from '../actions/sensors.actions';

const Labelcontainer = styled(FlexContainer)`
  padding-top: 1em;
  width: 100%;
  height: max-content;
  &:after {
    content: "";
    display: inline-block;
    height: 0.5em;
    align-self: center;
    width: Calc(100% - 25px);
    margin-right: -880px;
    margin-left: 10px;
    border-top: 1px solid ${props => props.theme.colors.greyD};
    margin-top: 0.5em;
  }
`;

const EmptyMsgContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 2em;
  margin: 2em;
  border: 2px dotted ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.utils.borderRadius};
  background-color: ${props => props.theme.colors.white};
  span {
    color: ${props => props.theme.colors.lightGray};
    font-weight: bold;
  }
`;
const Loader = styled.div`
  top: 0;
  left: 0;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  position: absolute;
`;

const Label = styled(Label_T)`
  padding-top: 1em;
  font-weight: 800;
`;

const H3 = styled(H3_T)`
  padding-left: 10px;
`;

const H4 = styled(H4_T)`
  padding-top:7px;
  font-weight:300;
  font-size:13px;
`;

const ChartContainer = styled.div`
  margin-bottom: 5px;
`;

function DeviceParameterAnalysis(props) {
  const { t } = useTranslation();
  const { getDeviceInfoLogs } = props.sensorActions;
  const { data, loading } = props.deviceLogsData;
  const { device_id } = props.deviceId;
  const serialNumber = props.serialNumber;
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [customDayFrom, setCustomDayFrom] = useState(null);
  const [customDayTo, setCustomDayTo] = useState(null);
  const [days, setDays] = useState('30');
  const [calenderValue, setCalenderValue] = useState('30');

  const calendarOptions = useMemo(() => [
    { value: '15', text: t('sensors.deviceParameterAnalysis.range15Days') },
    { value: '30', text: t('sensors.deviceParameterAnalysis.range30Days') },
    { value: '3months', text: t('sensors.deviceParameterAnalysis.range3Months') },
    { value: '1year', text: t('sensors.deviceParameterAnalysis.range1Year') },
    { value: 'custom', text: t('sensors.deviceParameterAnalysis.rangeCustom') }
  ], [t]);

  const calendarExpand = () => {
    setCalendarExpanded(!calendarExpanded);
  };

  useEffect(() => {
    setCalendarExpanded(false);
    getDeviceInfoLogs(serialNumber, days);
  }, [device_id]);

  const convertToTimestamps = (fromDate, toDate) => {
    const fromDateTimestamp = fromDate
      ? Math.floor(fromDate.getTime() / 1000)
      : null;
    const toDateTimestamp = toDate ? Math.floor(toDate.getTime() / 1000) : null;
    return { fromDateTimestamp, toDateTimestamp };
  };

  const calendarSelect = (value, fromDate, toDate) => {
    setCalenderValue(value);
    let selectedDays = value;

    if (value === 'custom') {
      setCustomDayFrom(fromDate);
      setCustomDayTo(toDate);
      selectedDays = 365;
      const { fromDateTimestamp, toDateTimestamp } = convertToTimestamps(
        fromDate,
        toDate
      );
      const formattedFromDate = fromDate ? fromDate.toLocaleDateString() : '';
      const formattedToDate = toDate ? toDate.toLocaleDateString() : '';
      setCalenderValue(`${formattedFromDate} - ${formattedToDate}`);
      getDeviceInfoLogs(
        serialNumber,
        selectedDays,
        fromDateTimestamp,
        toDateTimestamp,
      );
    } else if (value === '3months') {
      selectedDays = 90;
      getDeviceInfoLogs(serialNumber, selectedDays);
    } else if (value === '1year') {
      selectedDays = 365;
      getDeviceInfoLogs(serialNumber, selectedDays);
    } else {
      setCalenderValue(value);
      getDeviceInfoLogs(serialNumber, selectedDays);
    }
    setCalendarExpanded(false);
    setDays(selectedDays);
  };

  const {
    batteryVoltageData,
    coulombCountData,
    wifiRSSILevelData,
    measurementCycleTimeData,
    wifiConnectTimeData,
    dataExchangeTimeData,
    minCoulombCount,
    maxCoulombCount,
    minBatteryVoltage,
    maxBatteryVoltage,
    minDataExchangeTime,
    maxDataExchangeTime,
    minMeasurementCycleTime,
    maxMeasurementCycleTime,
    minWifiConnectTime,
    maxWifiConnectTime,
    minWifiRSSILevel,
    maxWifiRSSILevel,
  } = splitDeviceInfoLogs(data);

  const yAxisMinBatteryVoltage = minBatteryVoltage > 2.2 ? 2.2 : minBatteryVoltage - 0.2;
  const yAxisMaxBatteryVoltage = maxBatteryVoltage < 3.4 ? 3.4 : maxBatteryVoltage + 0.2;
  const yAxisMinWifiRSSILevel = minWifiRSSILevel < -10 ? -10 : minWifiRSSILevel + 2;
  const yAxisMaxWifiRSSILevel = maxWifiRSSILevel > -90 ? -90 : maxWifiRSSILevel - 2;
  const yAxisMinDataExchangeTime = minDataExchangeTime > 0 ? 0 : minDataExchangeTime - 2;
  const yAxisMaxDataExchangeTime = maxDataExchangeTime < 12 ? 12 : maxDataExchangeTime + 2;
  const yAxisMinWifiConnectTime = minWifiConnectTime > 0 ? 0 : minWifiConnectTime - 2;
  const yAxisMaxWifiConnectTime = maxWifiConnectTime < 60 ? 60 : maxWifiConnectTime + 2;
  const yAxisMinMeasurementCycleTime = minMeasurementCycleTime > 10 ? 10 : minMeasurementCycleTime - 2;
  const yAxisMaxMeasurementCycleTime = maxMeasurementCycleTime < 60 ? 60 : maxMeasurementCycleTime + 2;

  const ts = t('sensors.deviceParameterAnalysis.chartDataHeader.timestamp');
  const chartConfigurations = [
    {
      chartType: t('sensors.deviceParameterAnalysis.chartDataHeader.batteryVoltage'),
      data: [[ts, t('sensors.deviceParameterAnalysis.chartDataHeader.batteryVoltage')], ...batteryVoltageData],
      vAxisTitle: t('sensors.deviceParameterAnalysis.chartAxis.batteryVoltage'),
      yAxisMin: yAxisMinBatteryVoltage,
      yAxisMax: yAxisMaxBatteryVoltage,
      format: '#.#',
    },
    {
      chartType: t('sensors.deviceParameterAnalysis.chartDataHeader.coulombCount'),
      data: [[ts, t('sensors.deviceParameterAnalysis.chartDataHeader.coulombCount')], ...coulombCountData],
      vAxisTitle: t('sensors.deviceParameterAnalysis.chartAxis.coulombCount'),
      yAxisMin: minCoulombCount,
      yAxisMax: maxCoulombCount,
      format: '#',
    },
    {
      chartType: t('sensors.deviceParameterAnalysis.chartDataHeader.wifiRssi'),
      data: [[ts, t('sensors.deviceParameterAnalysis.chartDataHeader.wifiRssi')], ...wifiRSSILevelData],
      vAxisTitle: t('sensors.deviceParameterAnalysis.chartAxis.wifiRssi'),
      yAxisMin: yAxisMinWifiRSSILevel,
      yAxisMax: yAxisMaxWifiRSSILevel,
      format: '# dB',
    },
    {
      chartType: t('sensors.deviceParameterAnalysis.chartDataHeader.measurementCycleTime'),
      data: [
        [ts, t('sensors.deviceParameterAnalysis.chartDataHeader.measurementCycleTime')],
        ...measurementCycleTimeData
      ],
      vAxisTitle: t('sensors.deviceParameterAnalysis.chartAxis.measurementCycleTime'),
      yAxisMin: yAxisMinMeasurementCycleTime,
      yAxisMax: yAxisMaxMeasurementCycleTime,
      format: '# s',
    },
    {
      chartType: t('sensors.deviceParameterAnalysis.chartDataHeader.dataExchangeTime'),
      data: [[ts, t('sensors.deviceParameterAnalysis.chartDataHeader.dataExchangeTime')], ...dataExchangeTimeData],
      vAxisTitle: t('sensors.deviceParameterAnalysis.chartAxis.dataExchangeTime'),
      yAxisMin: yAxisMinDataExchangeTime,
      yAxisMax: yAxisMaxDataExchangeTime,
      format: '# s',
    },
    {
      chartType: t('sensors.deviceParameterAnalysis.chartDataHeader.wifiConnectTime'),
      data: [[ts, t('sensors.deviceParameterAnalysis.chartDataHeader.wifiConnectTime')], ...wifiConnectTimeData],
      vAxisTitle: t('sensors.deviceParameterAnalysis.chartAxis.wifiConnectTime'),
      yAxisMin: yAxisMinWifiConnectTime,
      yAxisMax: yAxisMaxWifiConnectTime,
      format: '# s',
    }
  ];

  function handleDownload() {
    const mergedData = {};

    const processData = (dataArray, key) => {
      dataArray.forEach(([timestamp, value]) => {
        if (!mergedData[timestamp]) {
          mergedData[timestamp] = { timestamp };
        }
        mergedData[timestamp][key] = value;
      });
    };

    processData(batteryVoltageData, 'battery_voltage');
    processData(coulombCountData, 'coulomb_count');
    processData(wifiRSSILevelData, 'wifi_rssi');
    processData(measurementCycleTimeData, 'measurement_cycle_time');
    processData(wifiConnectTimeData, 'wifi_connect_time');
    processData(dataExchangeTimeData, 'data_exchange_time');

    const structuredData = Object.values(mergedData).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const propertiesToHeaderMap = {
      timestamp: t('sensors.deviceParameterAnalysis.csvHeaders.timestamp'),
      battery_voltage: t('sensors.deviceParameterAnalysis.csvHeaders.battery_voltage'),
      coulomb_count: t('sensors.deviceParameterAnalysis.csvHeaders.coulomb_count'),
      wifi_rssi: t('sensors.deviceParameterAnalysis.csvHeaders.wifi_rssi'),
      measurement_cycle_time: t('sensors.deviceParameterAnalysis.csvHeaders.measurement_cycle_time'),
      wifi_connect_time: t('sensors.deviceParameterAnalysis.csvHeaders.wifi_connect_time'),
      data_exchange_time: t('sensors.deviceParameterAnalysis.csvHeaders.data_exchange_time')
    };

    const title = t('sensors.deviceParameterAnalysis.csvTitle', { serial: props.serialNumber });
    if (mergedData) downloadAsCSV(structuredData, propertiesToHeaderMap, `${title}.csv`);
  }

  const renderAllCharts = () => {
    if (!data || !data.data || data.data.length === 0) {
      return <EmptyMsgContainer>{t('sensors.deviceParameterAnalysis.noData')}</EmptyMsgContainer>;
    }

    // Parameters the device never reports are dropped upstream, so every chart can end up empty
    // even though the response carried logs.
    const plottableCharts = chartConfigurations.filter(config => config.data.length > 1);
    if (!plottableCharts.length) {
      return <EmptyMsgContainer>{t('sensors.deviceParameterAnalysis.noData')}</EmptyMsgContainer>;
    }

    return plottableCharts.map((config) => {
      const { chartType, data: chartData, vAxisTitle, yAxisMin, yAxisMax, format } = config;

      return (
        <ChartContainer key={chartType}>
          <Chart
            chartType="LineChart"
            data={chartData}
            height={300}
            width="100%"
            options={{
              curveType: 'function',
              legend: 'none',
              chartArea: { width: '80%' },
              vAxis: {
                title: vAxisTitle,
                format,
                viewWindow: {
                  min: yAxisMin,
                  max: yAxisMax
                }
              },
              hAxis: {
                textStyle: {
                  fontSize: 11,
                  bold: true
                },
                gridlines: { count: 5 },
                maxAlternation: 2,
              },
              explorer: {
                actions: ['dragToZoom', 'rightClickToReset'],
                axis: 'both',
                maxZoomIn: 0.001,
                maxZoomOut: 1
              },
              colors: ['rgb(145, 184, 70)']
            }}
          />
        </ChartContainer>
      );
    });
  };

  return (
    <Modal
      width="1100px"
      padding="0 10px"
      close={() => {
        props.onCancel();
      }}
      minHeight="500px"
    >
      <FlexContainer justifyContent="space-between" padding="10px">
        <H3>{t('sensors.deviceParameterAnalysis.title', { serial: props.serialNumber })}</H3>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CalendarFilter
                calendarExpanded={calendarExpanded}
                calendarSelect={calendarSelect}
                customDayFrom={customDayFrom}
                customDayTo={customDayTo}
                days={calenderValue}
                calendarOptions={calendarOptions}
                calendarExpand={calendarExpand}
              />
            </div>
            <div style={{ marginLeft: '10px', cursor: 'pointer' }} onClick={() => handleDownload()}>
              <DownloadSvg />
            </div>
            <div style={{ marginLeft: '10px' }}>
              <H4>{t('sensors.deviceParameterAnalysis.lastReportedAt', { time: props.last_report })}</H4>
            </div>
          </div>
      </FlexContainer>
      <Labelcontainer>
        <Label
          grey
          fontsize="14px"
          marginbottom="0"
          paddingtop="0"
          width="max-content"
        />
      </Labelcontainer>

      {!loading && renderAllCharts()}

      {loading && (
        <Loader>
          <LoadingSvg />
        </Loader>
      )}
    </Modal>
  );
}

const mapStateToProps = state => ({
  deviceLogsData: state.sensors.deviceLogsData
});

const mapDispatchToProps = dispatch => ({
  sensorActions: bindActionCreators(sensorActions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DeviceParameterAnalysis);
